import { useCallback, useEffect, useState } from 'react';
import { ChevronRightIcon, ShieldAlertIcon } from 'lucide-react';
import { app, notes, task, type NotesSnapshot, type ShushuPost } from '@/lib/bridge';
import { describeError } from '@/lib/errorMessage';
import { useAccounts } from '@/lib/useAccounts';
import { useAppInfo } from '@/lib/useAppInfo';
import { useAppReady } from '@/lib/useAppReady';
import { useGroupedSurface } from '@/lib/useGroupedSurface';
import { AccountCard } from '@/components/AccountCard';
import { PageShell } from '@/components/PageShell';
import { PageSkeleton } from '@/components/skeletons';
import { InfoCard, TaskProgressCard } from '@/components/task-ui';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CARD, HINT, MUTED } from '@/components/ui';
import { cn } from '@/lib/utils';

/**
 * 薯薯全能通 · 评论清理（`/shushu/comment-cleaner?accountId=`）。
 *
 * 对照搬小书 `mobile/tools/comment-cleaner`：选一篇笔记 → 确认 → 一键清空它的全部一级评论（回复随之删除）。
 * 执行在原生任务引擎（`notes.run clearComments`，删一条间隔约 2.5 秒，可暂停续跑、任务中心可见），页面只发起和展示。
 * 笔记列表来自本机（主页同步过的笔记），评论数是平台给的计数。平台无关：有 `commentsDelete` 能力的平台都能用。
 */

const MIN_BRIDGE = 6;

export function ShushuCommentCleanerPage() {
  const { info, loading: infoLoading, isNative } = useAppInfo();
  const { accounts, selectedId: accountId, loading: accountsLoading, error: accountError } = useAccounts();
  const [posts, setPosts] = useState<ShushuPost[] | null>(null);
  const [snapshot, setSnapshot] = useState<NotesSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  useGroupedSurface();

  const account = accounts.find((a) => a.id === accountId) ?? null;
  const outdated = !!info && info.bridgeVersion < MIN_BRIDGE;
  const supported = !!account?.capabilities?.includes('commentsDelete');
  const loaded = !infoLoading && !accountsLoading && (posts !== null || outdated || !accountId || error !== null);
  useAppReady(loaded);

  useEffect(() => {
    if (!accountId || outdated || infoLoading) return;
    let cancelled = false;
    Promise.all([notes.posts(accountId), notes.state(accountId)])
      .then(([result, state]) => {
        if (cancelled) return;
        setPosts(result.items.filter((p) => !p.deleted));
        if (state.action === 'clearComments') setSnapshot(state);
      })
      .catch((e) => {
        if (!cancelled) setError(describeError(e));
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, outdated, infoLoading, tick]);

  useEffect(
    () =>
      notes.onProgress((next) => {
        if (next.action !== 'clearComments' || (next as { accountId?: string }).accountId !== accountId) return;
        setSnapshot(next);
        if (next.state === 'running') return;
        void app.haptic(next.state === 'completed' && !next.failed ? 'success' : 'error');
        setTick((t) => t + 1);
      }),
    [accountId],
  );

  const running = snapshot?.state === 'running';
  const stopped = snapshot?.state === 'failed' || snapshot?.state === 'paused';
  const finished = snapshot?.state === 'completed';

  const clean = useCallback(
    async (post: ShushuPost) => {
      if (!accountId) return;
      const count = post.metrics.comments === undefined ? undefined : Math.round(post.metrics.comments);
      const confirmed = await app.confirm({
        title: '清空评论',
        message: `将删除「${post.title || '无标题'}」的${count ? `全部 ${count} 条` : '全部'}一级评论，每条的回复会一起删除，此操作不可恢复。每条间隔约 2.5 秒，评论多时需要几分钟。`,
        confirmLabel: '清空评论',
        destructive: true,
      });
      if (!confirmed) return;
      setBusy(true);
      setError(null);
      try {
        setSnapshot(await notes.run(accountId, 'clearComments', [post.id]));
      } catch (e) {
        setError(describeError(e));
      } finally {
        setBusy(false);
      }
    },
    [accountId],
  );

  const resume = useCallback(async () => {
    if (!snapshot?.taskId) return;
    setBusy(true);
    setError(null);
    try {
      await task.resume(snapshot.taskId);
      setSnapshot({ ...snapshot, state: 'running', error: undefined });
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }, [snapshot]);

  const done = snapshot?.done ?? 0;
  const failed = snapshot?.failed ?? 0;
  const total = Math.max(snapshot?.total ?? 0, done + failed);
  const withComments = (posts ?? []).filter((p) => p.metrics.comments === undefined || Math.round(p.metrics.comments) > 0);

  return (
    <PageShell title="评论清理" description="一键清空笔记下的全部评论">
      {!loaded ? <PageSkeleton kind="commentCleaner" /> : null}

      {loaded && !isNative ? (
        <InfoCard title="请在 App 内打开">浏览器里单独调试请加 <code>?mock=1</code>。</InfoCard>
      ) : null}
      {outdated ? (
        <InfoCard title="需要更新 App" icon={<ShieldAlertIcon className="text-primary size-5 shrink-0" />}>
          当前 App 版本还不支持评论清理，请更新后再试。
        </InfoCard>
      ) : null}

      {loaded && account && !outdated ? (
        <>
          <InfoCard title="注意">删除不可逆；删一条一级评论会连它下面的回复一起删除。</InfoCard>
          <AccountCard account={account} showId={false} />
        </>
      ) : null}

      {loaded && account && !outdated && !supported ? (
        <InfoCard title="暂不支持" icon={<ShieldAlertIcon className="text-primary size-5 shrink-0" />}>
          这个平台还不支持清理评论。
        </InfoCard>
      ) : null}

      {snapshot && (running || stopped || finished) ? (
        <TaskProgressCard
          title={running ? '正在清理评论' : stopped ? '已暂停' : '本次结果'}
          right={`${done + failed} / ${total}`}
          done={done + failed}
          total={total}
          finished={finished}
          paused={stopped}
          bar={!finished}
          action={
            <>
              <div className="text-muted-foreground flex gap-3.5 text-footnote tabular-nums">
                <span>已删除 {done}</span>
                <span className={cn(failed > 0 && 'text-destructive')}>失败 {failed}</span>
              </div>
              <p className={cn(HINT, stopped && 'text-destructive')}>
                {running
                  ? '每条间隔约 2.5 秒，可以离开本页，进度在任务中心也能看到。'
                  : stopped
                    ? `${snapshot.error ? describeError(snapshot.error) : '已暂停'}。继续后会接着删剩下的评论。`
                    : (snapshot.note ?? `已完成，删除 ${done} 条评论${failed ? `，${failed} 条失败（任务中心可看原因）` : ''}。`)}
              </p>
              {stopped ? (
                <Button className="w-full" disabled={busy} onClick={() => void resume()}>
                  继续
                </Button>
              ) : null}
            </>
          }
        />
      ) : null}

      {loaded && account && supported && posts ? (
        <Card className={CARD}>
          <p className="text-callout font-semibold">选择笔记</p>
          <p className={HINT}>点击笔记即可一键清理它的全部评论（会自动分批拉取并删除）。</p>
          <div className="flex flex-col gap-2">
            {withComments.map((post) => (
              <button
                key={post.id}
                type="button"
                className="bg-muted/60 flex w-full items-center gap-3 rounded-control px-3.5 py-3 text-left active:opacity-70 disabled:opacity-45"
                disabled={busy || running || !account.canWrite}
                onClick={() => void clean(post)}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-subheadline leading-snug">{post.title || '无标题'}</span>
                  <span className="text-muted-foreground text-footnote">
                    {post.metrics.comments === undefined ? '评论数未知' : `${Math.round(post.metrics.comments)} 条评论`}
                  </span>
                </span>
                <span className="text-primary shrink-0 text-caption1">一键清理</span>
                <ChevronRightIcon className="text-muted-foreground size-4 shrink-0" />
              </button>
            ))}
            {withComments.length === 0 ? <p className={cn('py-3', MUTED)}>没有可清理的笔记。</p> : null}
          </div>
        </Card>
      ) : null}

      {account && !account.canWrite && loaded ? (
        <p className={cn('mx-1', HINT, 'text-destructive')}>登录已失效，重新登录后才能清理评论。</p>
      ) : null}
      {(error ?? accountError) ? <p className="text-destructive px-1 text-footnote">{error ?? accountError}</p> : null}
    </PageShell>
  );
}
